import React, { createContext, useContext, useState } from "react";

const AuthCtx = createContext(null);

// Competition player auth lives in sessionStorage so it's cleared when the
// browser tab is closed. Other roles (admin, teacher) keep localStorage.
const PLAYER_STORAGE_KEYS = ["token", "role", "auth_extra"];
// Admin/backoffice session lives in its OWN dedicated slot so a student or
// competition login in the same browser can never overwrite it.
const ADMIN_STORAGE_KEYS = ["admin_token", "admin_role", "admin_extra"];

function readAuth() {
  // sessionStorage wins (player), otherwise fall back to localStorage (admin/teacher)
  const tokenS = sessionStorage.getItem("token");
  if (tokenS) {
    const role = sessionStorage.getItem("role");
    const extra = JSON.parse(sessionStorage.getItem("auth_extra") || "null");
    return { token: tokenS, role, ...(extra || {}) };
  }
  const token = localStorage.getItem("token");
  if (!token) return null;
  const role = localStorage.getItem("role");
  const extra = JSON.parse(localStorage.getItem("auth_extra") || "null");
  return { token, role, ...(extra || {}) };
}

function readAdminAuth() {
  const token = localStorage.getItem("admin_token");
  if (!token) return null;
  const role = localStorage.getItem("admin_role");
  const extra = JSON.parse(localStorage.getItem("admin_extra") || "null");
  return { token, role, ...(extra || {}) };
}

function clearAll() {
  for (const k of PLAYER_STORAGE_KEYS) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }
}

function clearAdmin() {
  for (const k of ADMIN_STORAGE_KEYS) localStorage.removeItem(k);
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readAuth);
  const [adminAuth, setAdminAuth] = useState(readAdminAuth);

  const login = (data) => {
    // Admin sessions are isolated in their own storage slot — never clobbered
    // by (and never clobbering) a student/competition login in the same browser.
    if (data.role === "admin") {
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_role", data.role);
      localStorage.setItem("admin_extra", JSON.stringify(data.extra || {}));
      setAdminAuth({ token: data.token, role: data.role, name: data.name, ...(data.extra || {}) });
      return;
    }
    clearAll();
    // Self-service "elim_user" lives in localStorage so users stay logged in
    // across tabs and browser restarts (longer journey than a competition).
    // "player" auth lives in sessionStorage (cleared on tab close).
    const storage = data.role === "player" ? sessionStorage : localStorage;
    storage.setItem("token", data.token);
    storage.setItem("role", data.role);
    storage.setItem("auth_extra", JSON.stringify(data.extra || {}));
    setAuth({ token: data.token, role: data.role, name: data.name, ...(data.extra || {}) });
  };

  const patchExtra = (patch) => {
    const cur = readAuth();
    if (!cur) return;
    const storage = cur.role === "player" ? sessionStorage : localStorage;
    const extra = JSON.parse(storage.getItem("auth_extra") || "{}");
    const next = { ...extra, ...(patch || {}) };
    storage.setItem("auth_extra", JSON.stringify(next));
    setAuth((a) => (a ? { ...a, ...(patch || {}) } : a));
  };

  const logout = () => {
    clearAll();
    // Also clear teacher-verified flags for any game
    try {
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith("teacher_verified_")) sessionStorage.removeItem(k);
      });
    } catch (_) {}
    setAuth(null);
  };

  const logoutAdmin = () => {
    clearAdmin();
    setAdminAuth(null);
  };

  return (
    <AuthCtx.Provider value={{ auth, adminAuth, login, logout, logoutAdmin, patchExtra }}>{children}</AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
